import { GridColDef } from "@mui/x-data-grid";
import { IUser } from "../store/slices/authSlice";
import { hasPermission } from "./permissions";
import EditImg from "../images/SideBar/Edit";
import DeleteImg from "../images/SideBar/Delete";
import React from "react";

/**
 * Common permission utilities for page components
 */
export interface PagePermissions {
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

/**
 * Get permissions for a specific resource
 */
export const getResourcePermissions = (user: IUser | null, resource: string): PagePermissions => {
  return {
    canCreate: hasPermission(user, resource, 'create'),
    canRead: hasPermission(user, resource, 'read'),
    canUpdate: hasPermission(user, resource, 'update'),
    canDelete: hasPermission(user, resource, 'delete'),
  };
};

/**
 * Create standard actions column for data grids
 */
export const createActionsColumn = (
  permissions: PagePermissions,
  handlers: {
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onView?: (id: string) => void;
  }
): GridColDef | null => {
  const { canUpdate, canDelete } = permissions;
  const { onEdit, onDelete, onView } = handlers;

  if (!canUpdate && !canDelete && !onView) {
    return null;
  }

  return {
    field: "Actions",
    headerName: "Actions",
    align: "center",
    headerAlign: "center",
    flex: 0.8,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      React.createElement('div', { className: "flex h-full items-center justify-center gap-3" }, [
        onView && React.createElement('button', {
          key: 'view',
          onClick: () => onView(params.row._id),
          className: "text-fontColor hover:text-blue-500",
          title: "View"
        }, React.createElement('span', null, '👁️')),
        
        canUpdate && onEdit && React.createElement('button', {
          key: 'edit',
          onClick: () => onEdit(params.row._id),
          className: "text-fontColor hover:text-primary-2",
          title: "Edit"
        }, React.createElement(EditImg)),
        
        canDelete && onDelete && React.createElement('button', {
          key: 'delete',
          onClick: () => onDelete(params.row._id),
          className: "text-fontColor hover:text-red-500",
          title: "Delete"
        }, React.createElement(DeleteImg))
      ])
    ),
  };
};

/**
 * Standard permission-based component wrapper
 */
export interface PermissionWrapperProps {
  user: IUser | null;
  resource: string;
  action: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const PermissionWrapper: React.FC<PermissionWrapperProps> = ({
  user,
  resource,
  action,
  children,
  fallback = null
}) => {
  const hasAccess = hasPermission(user, resource, action);
  return hasAccess ? React.createElement(React.Fragment, null, children) : React.createElement(React.Fragment, null, fallback);
};

/**
 * Common permission messages
 */
export const PERMISSION_MESSAGES = {
  NO_CREATE_ACCESS: "You don't have permission to create new items.",
  NO_UPDATE_ACCESS: "You don't have permission to edit this item.",
  NO_DELETE_ACCESS: "You don't have permission to delete this item.",
  NO_READ_ACCESS: "You don't have permission to view this content.",
  ADMIN_ONLY: "This action is only available to administrators.",
  SUPER_ADMIN_ONLY: "This action is only available to super administrators.",
};

/**
 * Get appropriate delete confirmation message based on resource
 */
export const getDeleteConfirmationMessage = (resource: string): string => {
  const resourceMap: Record<string, string> = {
    language: "Are you sure you want to delete this language?",
    education: "Are you sure you want to delete this education level?",
    stream: "Are you sure you want to delete this stream?",
    subject: "Are you sure you want to delete this subject?",
    'test-series': "Are you sure you want to delete this test series?",
    question: "Are you sure you want to delete this question?",
    ebook: "Are you sure you want to delete this ebook?",
    article: "Are you sure you want to delete this article?",
    'article-category': "Are you sure you want to delete this category?",
    banner: "Are you sure you want to delete this banner?",
    coupon: "Are you sure you want to delete this coupon?",
    'coupon-category': "Are you sure you want to delete this coupon category?",
    student: "Are you sure you want to delete this student account?",
  };
  
  return resourceMap[resource] || `Are you sure you want to delete this ${resource}?`;
};

/**
 * Standard add button text based on resource
 */
export const getAddButtonText = (resource: string): string => {
  const resourceMap: Record<string, string> = {
    language: "Add Language",
    education: "Add Education",
    stream: "Add Stream", 
    subject: "Add Subject",
    'test-series': "Add Test Series",
    question: "Add Question",
    ebook: "Add Ebook",
    article: "Add Article",
    'article-category': "Add Category",
    banner: "Add Banner",
    coupon: "Add Coupon",
    'coupon-category': "Add Category",
    student: "Add Student",
  };
  
  return resourceMap[resource] || `Add ${resource}`;
};